'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { useLazyQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { columns } from './components/columns'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { Button } from '@/components/ui/button'
import {
  DELETE_MANY_PRODUCT_FEATURES,
  GET_PRODUCT_FEATURES,
} from './productFeaturesQueries'
import ProductFeatureForm from '@/containers/ProductFeatures/components/ProductFeatureForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'

export default function ProductFeatures() {
  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  )

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  })

  const [loadProductFeatures, { data, error, loading }] = useLazyQuery(
    GET_PRODUCT_FEATURES,
    {
      variables: {},
      fetchPolicy: 'cache-and-network',
    }
  )

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_PRODUCT_FEATURES,
    refetchQueries: ['GET_PRODUCT_FEATURES'],
    messages: {
      success: 'Product features deleted successfully',
      error: 'Failed to delete product features',
    },
    itemName: 'product feature',
  })

  // Load product features data
  useEffect(() => {
    loadProductFeatures({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    })
  }, [searchTerm, pagination, order])

  // Reset to first page when search or page size changes
  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
  }, [searchTerm, pagination.pageSize, order])

  // Update table data when query results change
  useEffect(() => {
    if (data) {
      setTableData(data.findManyProductFeature)
      setTableDataCount(data.findManyProductFeatureCount ?? 0)
    }
  }, [data])

  const openProductFeatureForm = useOpenDrawer(ProductFeatureForm)

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Product Features" />
        <Button onClick={openProductFeatureForm}>Create New +</Button>
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={openProductFeatureForm}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Filter product features...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [], // Add empty columns array to satisfy type requirement
            }}
          />
        )}
      />
    </PageContent>
  )
}
